import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PropChallenge } from 'services/generatedClientFromSwagger';
import { KeeperType } from 'features/prop/store/plans/interfaces';
import useDeviceTypeKeeper from 'hooks/useDeviceTypeKeeper';
import { prop_myPlans_getSelectedChallenge } from 'features/prop/store/plans/selectors';
import type { RootState } from 'store';

export const useMixpanelData = (keeperType?: KeeperType, challenge?: PropChallenge) => {
    const userEmail = useSelector((state: RootState) => state.user?.userInfo?.email);

    const selectedChallenge = useSelector<RootState, PropChallenge | undefined>(prop_myPlans_getSelectedChallenge);
    const challengeToUse = challenge ?? selectedChallenge;

    const maxDailyKeeper = challengeToUse?.keeper?.maxDaily;
    const maxTotalKeeper = challengeToUse?.keeper?.maxTotal;

    const plans = useSelector((state: RootState) => state?.prop_myPlans.plans);
    const plan = plans.find((p) => p.propChallenges?.some((c) => c.id === challengeToUse?.id));
    const originalAdditionalInfo = plan?.originalAdditionalInfo;

    const keeperMaxType =
        maxDailyKeeper && maxDailyKeeper?.hit
            ? KeeperType.MAX_DAILY
            : maxTotalKeeper && maxTotalKeeper?.hit
              ? KeeperType.MAX_TOTAL
              : undefined;

    const keeperTypeToUse = keeperType ?? keeperMaxType;
    const device = useDeviceTypeKeeper();

    return useMemo(() => {
        const base = {
            accountEmail: userEmail,
            planName: challengeToUse?.name,
            device,
        };

        switch (keeperTypeToUse) {
            case KeeperType.MAX_DAILY:
                return {
                    ...base,
                    conditionName: KeeperType.MAX_DAILY,
                    keeperPrice: maxDailyKeeper?.priceValue,
                    originalValue: Math.abs(originalAdditionalInfo?.challenges?.propsEngine?.maxDailyLossPercent ?? 0),
                    newValue: Math.abs(maxDailyKeeper?.conditionValue ?? 0),
                };

            case KeeperType.MAX_TOTAL:
                return {
                    ...base,
                    conditionName: KeeperType.MAX_TOTAL,
                    keeperPrice: maxTotalKeeper?.priceValue,
                    originalValue: Math.abs(originalAdditionalInfo?.challenges?.propsEngine?.shutdownPercent ?? 0),
                    newValue: Math.abs(maxTotalKeeper?.conditionValue ?? 0),
                };

            default:
                return base;
        }
    }, [userEmail, challengeToUse, keeperType, device]);
};
